/**
 * BriefsReport
 *
 * Org-level Briefs surface (see Documentation/BRIEFS_PRD.md). One control set —
 * workspace(s) multi-select + date range + client-facing toggle — drives a
 * Client Brief (one workspace) or Portfolio Brief (many). Read-only assembly
 * from GET /api/org-reports/:orgId/brief.
 *
 * Position: bills show the position AS OF the range end; legislators /
 * candidates / initiatives have no position history, so they show CURRENT
 * position, labeled "(current)".
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckSquare, Square, Printer, Loader2, Calendar as CalendarIcon,
  FileText, Users, UserCheck, Vote, StickyNote, AlertCircle,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../../lib/api';
import Chip from '../Chip';
import { HelpTrigger } from '../../help';
import { StandardTableHeader } from '../StandardTable';
import { padDistrictNumber } from '../../utils/districtDisplay';

interface BriefsReportProps {
  organizationId: string;
  organizationName?: string;
  organizationLogoUrl?: string;
}

interface WorkspaceOption { id: string; name: string; }

type ChipVariant = 'success' | 'danger' | 'warning' | 'neutral' | 'info' | 'secondary';

interface BriefBill {
  billId: number; number: string; title: string; session?: string; status?: string | null;
  position?: string | null; positionAsOf: boolean;
  lastAction?: string | null; lastActionDate?: string | null; sponsor?: string | null;
  nextHearing?: string | null; activeInRange: boolean; newInRange: boolean;
  positionChangedInRange: boolean; positionChange?: { from?: string; to?: string } | null;
}
interface BriefLegislator {
  legislatorId: number; name: string; chamber?: string | null; district?: string | null;
  party?: string | null; position?: string | null; isPriority: boolean;
}
interface BriefCandidate {
  candidateId: string; name: string; office?: string | null; district?: string | null;
  party?: string | null; electionYear?: number | null; status?: string | null;
  position?: string | null; isPriority: boolean;
}
interface BriefInitiative {
  initiativeId: string; number?: string | null; title?: string | null; status?: string | null;
  electionYear?: number | null; position?: string | null; isHot: boolean;
}
interface BriefCalendarItem {
  calendarId: number; date: string; time?: string | null; title?: string | null;
  chamber?: string | null; isCommittee: boolean; meetingType?: string | null;
  room?: string | null; billNumber?: string | null; upcoming: boolean;
}
interface BriefNote {
  id: string; title?: string | null; content: string; author?: string | null;
  createdAt: string; entities: Array<{ type?: string; name?: string }>;
}
interface WorkspaceBrief {
  workspace: { id: string; name: string };
  summary: {
    bills: number; billsActiveInRange: number; billsNewInRange: number;
    legislators: number; candidates: number; initiatives: number; hearings: number; notes: number;
  };
  calendar: BriefCalendarItem[];
  entities: { bills: BriefBill[]; legislators: BriefLegislator[]; candidates: BriefCandidate[]; initiatives: BriefInitiative[] };
  notes: BriefNote[];
}
interface BriefPayload {
  meta: {
    type: 'client' | 'portfolio';
    workspaces: Array<{ id: string; name: string }>;
    range: { start: string | null; end: string | null };
    clientFacing: boolean;
    generatedAt: string;
  };
  perWorkspace: WorkspaceBrief[];
}

function positionVariant(p?: string | null): ChipVariant {
  const s = (p || '').toLowerCase();
  if (s.includes('support')) return 'success';
  if (s.includes('oppos')) return 'danger';
  if (s.includes('monitor')) return 'warning';
  return 'neutral';
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PositionCell: React.FC<{ position?: string | null; asOf: boolean }> = ({ position, asOf }) => {
  if (!position) return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <Chip label={position} variant={positionVariant(position)} size="sm" />
      {!asOf && (
        <span className="text-[10px] text-text-tertiary dark:text-text-dark-tertiary" title="Current position; no dated history for this entity type">
          (current)
        </span>
      )}
    </span>
  );
};

const SectionHeading: React.FC<{ icon: React.ElementType; children: React.ReactNode; count?: number }> = ({ icon: Icon, children, count }) => (
  <h4 className="flex items-center gap-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary mt-5 mb-2">
    <Icon className="w-4 h-4 text-primary-600" />
    {children}
    {typeof count === 'number' && <span className="text-text-tertiary dark:text-text-dark-tertiary font-normal">({count})</span>}
  </h4>
);

const th = 'text-left text-[11px] uppercase tracking-wide text-text-secondary dark:text-text-dark-secondary font-semibold px-3 py-2';
const td = 'px-3 py-2 text-sm text-text-primary dark:text-text-dark-primary align-top';

const WorkspaceSection: React.FC<{ wb: WorkspaceBrief }> = ({ wb }) => {
  const { entities, calendar, notes, summary } = wb;
  const hasAny = summary.bills || summary.legislators || summary.candidates || summary.initiatives || calendar.length || notes.length;
  return (
    <section className="border border-border-light dark:border-border-dark rounded-lg p-4 mb-4 break-inside-avoid">
      <h3 className="text-lg font-bold text-text-primary dark:text-text-dark-primary">{wb.workspace.name}</h3>
      <p className="text-xs text-text-secondary dark:text-text-dark-secondary mb-3">
        {summary.bills} bills · {summary.legislators} legislators · {summary.candidates} candidates · {summary.initiatives} initiatives
        {summary.billsActiveInRange > 0 && ` · ${summary.billsActiveInRange} active this period`}
      </p>

      {!hasAny && <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary italic">Nothing tracked in range for this workspace.</p>}

      {/* Calendar */}
      {calendar.length > 0 && (
        <>
          <SectionHeading icon={CalendarIcon} count={calendar.length}>Calendar &amp; Hearings</SectionHeading>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <StandardTableHeader variant="report"><tr>
                <th className={th}>Date</th><th className={th}>Bill</th><th className={th}>Body</th><th className={th}>Title</th><th className={th}>When</th>
              </tr></StandardTableHeader>
              <tbody>
                {calendar.map(c => (
                  <tr key={`${c.calendarId}-${c.billNumber}`} className="border-b border-border-light/60 dark:border-border-dark/50">
                    <td className={td}>{fmtDate(c.date)}{c.time ? ` ${c.time}` : ''}</td>
                    <td className={td}>{c.billNumber || '—'}</td>
                    <td className={td}>{[c.chamber, c.isCommittee ? 'Committee' : null].filter(Boolean).join(' · ') || '—'}</td>
                    <td className={td}>{c.title || '—'}</td>
                    <td className={td}>{c.upcoming ? <Chip label="Upcoming" variant="info" size="sm" /> : <span className="text-text-tertiary">In range</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Bills */}
      {entities.bills.length > 0 && (
        <>
          <SectionHeading icon={FileText} count={entities.bills.length}>Bills</SectionHeading>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <StandardTableHeader variant="report"><tr>
                <th className={th}>Bill</th><th className={th}>Position</th><th className={th}>Status</th><th className={th}>Last action</th><th className={th}>Next hearing</th><th className={th}>Sponsor</th>
              </tr></StandardTableHeader>
              <tbody>
                {entities.bills.map(b => (
                  <tr key={b.billId} className="border-b border-border-light/60 dark:border-border-dark/50">
                    <td className={td}>
                      <span className="font-medium text-primary-700 dark:text-primary-400">{b.number}</span>
                      <div className="text-xs text-text-secondary dark:text-text-dark-secondary max-w-md">{b.title}</div>
                      <div className="flex gap-1 mt-1">
                        {b.newInRange && <Chip label="New" variant="info" size="sm" />}
                        {b.positionChangedInRange && b.positionChange && (
                          <Chip label={`${b.positionChange.from || '—'} → ${b.positionChange.to || '—'}`} variant="warning" size="sm" />
                        )}
                      </div>
                    </td>
                    <td className={td}><PositionCell position={b.position} asOf={b.positionAsOf} /></td>
                    <td className={td}>{b.status || '—'}</td>
                    <td className={td}>{b.lastAction ? <span title={b.lastActionDate || ''}>{b.lastAction}</span> : '—'}</td>
                    <td className={td}>{fmtDate(b.nextHearing)}</td>
                    <td className={td}>{b.sponsor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Legislators */}
      {entities.legislators.length > 0 && (
        <>
          <SectionHeading icon={Users} count={entities.legislators.length}>Legislators</SectionHeading>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <StandardTableHeader variant="report"><tr>
                <th className={th}>Name</th><th className={th}>Chamber / District</th><th className={th}>Party</th><th className={th}>Position</th>
              </tr></StandardTableHeader>
              <tbody>
                {entities.legislators.map(l => (
                  <tr key={l.legislatorId} className="border-b border-border-light/60 dark:border-border-dark/50">
                    <td className={td}>{l.name}{l.isPriority && <span className="text-yellow-500" title="Priority"> ★</span>}</td>
                    <td className={td}>{[l.chamber, padDistrictNumber(l.district)].filter(Boolean).join(' · ') || '—'}</td>
                    <td className={td}>{l.party || '—'}</td>
                    <td className={td}><PositionCell position={l.position} asOf={false} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Candidates */}
      {entities.candidates.length > 0 && (
        <>
          <SectionHeading icon={UserCheck} count={entities.candidates.length}>Candidates</SectionHeading>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <StandardTableHeader variant="report"><tr>
                <th className={th}>Name</th><th className={th}>Office</th><th className={th}>Party</th><th className={th}>Status</th><th className={th}>Position</th>
              </tr></StandardTableHeader>
              <tbody>
                {entities.candidates.map(c => (
                  <tr key={c.candidateId} className="border-b border-border-light/60 dark:border-border-dark/50">
                    <td className={td}>{c.name}{c.isPriority && <span className="text-yellow-500" title="Priority"> ★</span>}</td>
                    <td className={td}>{[c.office, padDistrictNumber(c.district)].filter(Boolean).join(' · ') || '—'}</td>
                    <td className={td}>{c.party || '—'}</td>
                    <td className={td}>{c.status || '—'}</td>
                    <td className={td}><PositionCell position={c.position} asOf={false} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Initiatives */}
      {entities.initiatives.length > 0 && (
        <>
          <SectionHeading icon={Vote} count={entities.initiatives.length}>Ballot Initiatives</SectionHeading>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <StandardTableHeader variant="report"><tr>
                <th className={th}>Measure</th><th className={th}>Status</th><th className={th}>Election</th><th className={th}>Position</th>
              </tr></StandardTableHeader>
              <tbody>
                {entities.initiatives.map(i => (
                  <tr key={i.initiativeId} className="border-b border-border-light/60 dark:border-border-dark/50">
                    <td className={td}>{[i.number, i.title].filter(Boolean).join(' — ') || '—'}{i.isHot && <span className="text-error-500" title="Hot"> 🔥</span>}</td>
                    <td className={td}>{i.status || '—'}</td>
                    <td className={td}>{i.electionYear || '—'}</td>
                    <td className={td}><PositionCell position={i.position} asOf={false} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Notes */}
      {notes.length > 0 && (
        <>
          <SectionHeading icon={StickyNote} count={notes.length}>Notes</SectionHeading>
          <ul className="space-y-2">
            {notes.map(n => (
              <li key={n.id} className="text-sm border-l-2 border-primary-300 pl-3">
                <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                  {fmtDate(n.createdAt)}{n.author ? ` · ${n.author}` : ''}
                  {n.entities.length > 0 && ` · ${n.entities.map(e => e.name).filter(Boolean).join(', ')}`}
                </div>
                {n.title && <div className="font-medium text-text-primary dark:text-text-dark-primary">{n.title}</div>}
                {/* Note content is rich-text HTML from the in-app editor; sanitize before rendering. */}
                <div
                  className="text-text-primary dark:text-text-dark-primary prose prose-sm max-w-none break-words"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.content) }}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

const BriefsReport: React.FC<BriefsReportProps> = ({ organizationId, organizationName, organizationLogoUrl }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(isoDaysAgo(30));
  const [endDate, setEndDate] = useState<string>(todayIso());
  const [clientFacing, setClientFacing] = useState<boolean>(false);
  const [brief, setBrief] = useState<BriefPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: workspaces = [], isLoading: wsLoading } = useQuery<WorkspaceOption[]>(
    ['brief-workspaces', organizationId],
    async () => {
      const res = await api.get(`/org-reports/${organizationId}/workspaces`);
      const rows = res.data?.data || [];
      return rows.map((r: { id: string; name: string }) => ({ id: r.id, name: r.name }));
    },
    {
      staleTime: 5 * 60 * 1000,
      onSuccess: (rows) => {
        // Default to all workspaces selected (portfolio) on first load.
        setSelected(prev => (prev.length === 0 ? rows.map(r => r.id) : prev));
      },
    },
  );

  const rangeInvalid = !!startDate && !!endDate && new Date(startDate) > new Date(endDate);
  const canGenerate = selected.length > 0 && !rangeInvalid && !loading;

  const toggleWorkspace = (id: string) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const selectAll = () => setSelected(workspaces.map(w => w.id));
  const deselectAll = () => setSelected([]);

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        workspace_ids: selected.join(','),
        start_date: startDate,
        end_date: endDate,
        client_facing: String(clientFacing),
      });
      const res = await api.get(`/org-reports/${organizationId}/brief?${params.toString()}`);
      if (res.data?.success) {
        setBrief(res.data.data as BriefPayload);
      } else {
        setError(res.data?.error || 'Failed to generate brief');
      }
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Failed to generate brief');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!brief) return;
    setDownloading(true);
    setError(null);
    try {
      const res = await api.post(
        `/org-reports/${organizationId}/brief/export`,
        {
          workspaceIds: brief.meta.workspaces.map(w => w.id),
          startDate: brief.meta.range.start,
          endDate: brief.meta.range.end,
          clientFacing: brief.meta.clientFacing,
          organizationLogoUrl: organizationLogoUrl || null,
        },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = (res.headers as Record<string, string>)['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : 'brief.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const briefType = brief?.meta.type === 'client' ? 'Client Brief' : 'Portfolio Brief';
  const workspaceNames = useMemo(
    () => (brief ? brief.meta.workspaces.map(w => w.name).join(', ') : ''),
    [brief],
  );

  return (
    <div className="space-y-4">
      {/* Controls (hidden when printing) */}
      <div className="print:hidden space-y-4">
        <div className="flex items-center gap-1.5 text-sm text-text-secondary dark:text-text-dark-secondary">
          <span>Build a date-ranged brief across the workspaces you choose.</span>
          <HelpTrigger contentKey="briefs-overview" size="sm" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Workspaces</label>
            <div className="flex gap-3 text-xs">
              <button onClick={selectAll} className="text-primary-600 hover:text-primary-700 font-medium">Select all</button>
              <button onClick={deselectAll} className="text-text-secondary hover:text-text-primary font-medium">Deselect all</button>
            </div>
          </div>
          {wsLoading ? (
            <div className="text-sm text-text-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading workspaces…</div>
          ) : workspaces.length === 0 ? (
            <p className="text-sm text-text-tertiary">No workspaces available.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-52 overflow-y-auto">
              {workspaces.map(w => {
                const checked = selected.includes(w.id);
                return (
                  <button key={w.id} onClick={() => toggleWorkspace(w.id)}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded border text-left text-sm ${checked ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-border-light dark:border-border-dark'}`}>
                    {checked ? <CheckSquare className="w-4 h-4 text-primary-600 flex-none" /> : <Square className="w-4 h-4 text-text-tertiary flex-none" />}
                    <span className="truncate text-text-primary dark:text-text-dark-primary">{w.name}</span>
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-text-tertiary mt-1">
            {selected.length === 1 ? 'One workspace → Client Brief.' : `${selected.length} workspaces → Portfolio Brief.`}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Start date</label>
            <input type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">End date</label>
            <input type="date" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)} className="input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-primary dark:text-text-dark-primary cursor-pointer">
            <input type="checkbox" checked={clientFacing} onChange={e => setClientFacing(e.target.checked)} className="rounded border-border-light text-primary-600" />
            Client-facing (hide internal notes)
            <HelpTrigger contentKey="briefs-client-facing" size="sm" />
          </label>
          <button onClick={generate} disabled={!canGenerate}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white text-sm font-medium">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Generate brief
          </button>
          {brief && (
            <button onClick={downloadPdf} disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border-light dark:border-border-dark text-sm font-medium text-text-primary dark:text-text-dark-primary disabled:opacity-60">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Export PDF
            </button>
          )}
        </div>

        {rangeInvalid && (
          <p className="flex items-center gap-2 text-sm text-error-600"><AlertCircle className="w-4 h-4" /> Start date must be on or before end date.</p>
        )}
        {error && (
          <p className="flex items-center gap-2 text-sm text-error-600"><AlertCircle className="w-4 h-4" /> {error}</p>
        )}
      </div>

      {/* Brief output */}
      {brief && (
        <div className="brief-output">
          {/* Cover */}
          <div className="flex items-start justify-between border-b border-border-light dark:border-border-dark pb-4 mb-4">
            <div>
              <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">{briefType}</h2>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{workspaceNames}</p>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {fmtDate(brief.meta.range.start)} – {fmtDate(brief.meta.range.end)}
              </p>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                Generated {fmtDate(brief.meta.generatedAt)} · {organizationName}
                {brief.meta.clientFacing && ' · client-facing'}
              </p>
            </div>
            {organizationLogoUrl && (
              <img src={organizationLogoUrl} alt={organizationName || 'Organization'} className="max-h-16 max-w-[180px] object-contain" />
            )}
          </div>

          {/* Portfolio roll-up */}
          {brief.meta.type === 'portfolio' && brief.perWorkspace.length > 1 && (
            <div className="overflow-x-auto mb-5">
              <table className="min-w-full border-collapse">
                <StandardTableHeader variant="report"><tr>
                  <th className={th}>Workspace</th><th className={th}>Bills</th><th className={th}>Active</th><th className={th}>Legislators</th><th className={th}>Candidates</th><th className={th}>Initiatives</th><th className={th}>Hearings</th>
                </tr></StandardTableHeader>
                <tbody>
                  {brief.perWorkspace.map(wb => (
                    <tr key={wb.workspace.id} className="border-b border-border-light/60 dark:border-border-dark/50">
                      <td className={td}>{wb.workspace.name}</td>
                      <td className={td}>{wb.summary.bills}</td>
                      <td className={td}>{wb.summary.billsActiveInRange}</td>
                      <td className={td}>{wb.summary.legislators}</td>
                      <td className={td}>{wb.summary.candidates}</td>
                      <td className={td}>{wb.summary.initiatives}</td>
                      <td className={td}>{wb.summary.hearings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Per-workspace sections */}
          {brief.perWorkspace.map(wb => <WorkspaceSection key={wb.workspace.id} wb={wb} />)}
        </div>
      )}

      {!brief && !loading && (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary print:hidden">
          Pick workspaces and a date range, then Generate a brief.
        </p>
      )}
    </div>
  );
};

export default BriefsReport;
